import { useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import type { JSX, ReactNode } from 'react';

import { EMPTY_ID } from '@e-socna/empty-client';
import { DUMMY_MISSION_ID } from '@e-socna/flags';
import type { Id } from '@e-socna/data-layer';

import { useFindOneMissionQuery } from '../../pages/Mission/hooks/useFindOneMissionQuery.hook';
import { useFindOneResolvableMissionTypeQuery } from '../../pages/ResolvableMissionType/hooks/useFindOneResolvableMissionTypeQuery.hook';

import { useFolderContext } from '../FolderContext/hooks/useFolderContext.hook';
import { MissionWarningsContextProvider } from '../MissionWarningsContext';

import { MissionContext } from '.';
import { MissionContextStatus } from './types/mission-context.type';
import type { MissionContextData } from './types/mission-context.type';

interface Props {
  readonly children?: ReactNode;
  // A print page: the printer clicks its controls, no notification may cover them.
  readonly isInPrintMode?: boolean;
}

/**
 * Provides a MissionContext backed by the DUMMY_MISSION.
 * Must be nested inside DummyFolderContextProvider.
 * Used for template pages (section & document) so that resolvers
 * have a valid business context when generating PDFs.
 * Does not interact with navigation context (no setMissionSpaceInfo).
 */
const DummyMissionContextProvider = ({
  children,
  isInPrintMode = false,
}: Readonly<Props>): JSX.Element => {
  const { folder, folderId } = useFolderContext();

  const {
    data: missionQueryData,
    isLoading: isMissionLoading,
    isError: isMissionError,
  } = useFindOneMissionQuery(DUMMY_MISSION_ID as Id);
  const mission = missionQueryData?.findOneMission;

  const resolvableMissionTypeId = mission?.typeId;
  const {
    data: resolvableMissionTypeData,
    isLoading: isTypeLoading,
    isError: isTypeError,
  } = useFindOneResolvableMissionTypeQuery(resolvableMissionTypeId ?? EMPTY_ID);
  const resolvableMissionType =
    resolvableMissionTypeData?.findOneResolvableMissionType;

  const isLoading = isMissionLoading || (mission?.typeId && isTypeLoading);
  const isError = isMissionError || isTypeError;

  const contextValue = useMemo<MissionContextData>(() => {
    if (isLoading) {
      return {
        status: MissionContextStatus.Loading,
        missionId: DUMMY_MISSION_ID as Id,
        folderId,
        resolvableMissionTypeId: undefined,
        mission: undefined,
        folder,
        resolvableMissionType: undefined,
      };
    }

    if (isError) {
      return {
        status: MissionContextStatus.Error,
        missionId: DUMMY_MISSION_ID as Id,
        folderId,
        resolvableMissionTypeId: undefined,
        mission: undefined,
        folder,
        resolvableMissionType: undefined,
      };
    }

    if (!mission || !folder || !folderId) {
      return {
        status: MissionContextStatus.Loading,
        missionId: DUMMY_MISSION_ID as Id,
        folderId,
        resolvableMissionTypeId: undefined,
        mission: undefined,
        folder,
        resolvableMissionType: undefined,
      };
    }

    return {
      status: MissionContextStatus.Ready,
      missionId: DUMMY_MISSION_ID as Id,
      folderId,
      resolvableMissionTypeId,
      mission,
      folder,
      resolvableMissionType,
      relatedBusinessEntities: {
        folder,
        mission,
        resolvableMissionType,
      },
    };
  }, [
    isLoading,
    isError,
    folderId,
    resolvableMissionTypeId,
    mission,
    folder,
    resolvableMissionType,
  ]);

  return (
    <MissionContext.Provider value={contextValue}>
      <MissionWarningsContextProvider isSilent={isInPrintMode}>
        {children ?? <Outlet />}
      </MissionWarningsContextProvider>
    </MissionContext.Provider>
  );
};

export { DummyMissionContextProvider };
